import React from "react";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { TTSCustomEditorColors } from "shared/constants";
import { ReactComponent as UndoIcon } from "assets/icons/tts-custom-editor/undo.svg";
import { ReactComponent as RedoIcon } from "assets/icons/tts-custom-editor/redo.svg";
import { useTranslation } from "react-i18next";
import CustomIconMenu from "../../components/CustomIconMenu";
import { isHtmlViewAtom } from "atoms";
import { useAtomValue } from "jotai";

interface UndoRedoMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
}

export default function UndoRedoMenu({
  editorRef,
}: UndoRedoMenuProps): JSX.Element {
  const { t } = useTranslation();
  const hasUndo = editorRef.current?.editor?.undoManager?.hasUndo() || false;
  const hasRedo = editorRef.current?.editor?.undoManager?.hasRedo() || false;
  const isHtmlView = useAtomValue(isHtmlViewAtom);

  const redoContent = () => {
    editorRef.current?.editor?.execCommand("redo");
  };
  const undoContent = () => {
    const editor = editorRef.current?.editor;
    if (editor) {
      editor.execCommand("undo");
      editor.focus();
      // If the editor is not empty, move cursor to the end of the content
      // this handles when the editor is cleared and undo is clicked
      editor.selection.select(editor.getBody(), true);
      editor.selection.collapse(false);
    }
  };

  return (
    <>
      <CustomIconMenu
        title={t("tts.toolbarMenu.undo")}
        id="undo-button"
        component={UndoIcon}
        onClick={undoContent}
        isActive={false}
        color={
          hasUndo && !isHtmlView
            ? TTSCustomEditorColors.ToolbarIconColor
            : TTSCustomEditorColors.IconDisabledColor
        }
        buttonStyles={{
          cursor: !hasUndo ? "default" : "pointer",
        }}
        disabled={isHtmlView}
      />

      <CustomIconMenu
        title={t("tts.toolbarMenu.redo")}
        id="redo-button"
        component={RedoIcon}
        onClick={redoContent}
        isActive={false}
        color={
          hasRedo && !isHtmlView
            ? TTSCustomEditorColors.ToolbarIconColor
            : TTSCustomEditorColors.IconDisabledColor
        }
        buttonStyles={{
          cursor: !hasRedo ? "default" : "pointer",
        }}
        disabled={isHtmlView}
      />
    </>
  );
}
